/**
 * VisitsScheduleView — встроенный view страницы «Расписание выездов»
 * внутри чат-топика kind='visits_schedule'. Аналог ReportsView /
 * FocalDealsView (см. project_kb_chat_architecture).
 *
 * Шапка: аватар + название + «Настроить» (EditGroupModal). Под шапкой
 * сам календарь VisitsPage из дашборда — изменения летят в общий
 * /api/visits на бэкенде, как из дашборда.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { EditGroupModal } from "@/components/EditGroupModal";
import { topicAvatarUrl, type ChatTopic } from "@/api/dashboard";
import { parseTopicIcon } from "@/data/animatedIcons";
import { CrmFrame } from "@/components/CrmFrame";


export function VisitsScheduleView({ topic, canModerate, myUserId }: {
  topic: ChatTopic;
  canModerate: boolean;
  myUserId: string | null;
}) {
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const avatarUrl = topic.avatar_filename ? topicAvatarUrl(topic.id, topic.avatar_filename) : null;
  const parsedIcon = !avatarUrl ? parseTopicIcon(topic.icon) : { kind: "none" as const };

  return (
    <div style={{
      flex: 1, minHeight: 0,
      display: "flex", flexDirection: "column",
      background: "var(--paper, #f4f4f5)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "14px 20px", borderBottom: "1px solid var(--line)",
        background: "#fff",
      }}>
        <button
          type="button"
          onClick={() => canModerate && setEditOpen(true)}
          disabled={!canModerate}
          title={canModerate ? "Изменить аватар" : ""}
          style={{
            width: 44, height: 44, borderRadius: 12,
            background: avatarUrl ? `url(${avatarUrl}) center/cover no-repeat`
                                  : "linear-gradient(135deg, #4fc3f7 0%, #1976d2 100%)",
            border: "none", padding: 0,
            display: "grid", placeItems: "center",
            color: "#fff",
            cursor: canModerate ? "pointer" : "default",
            flexShrink: 0,
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
        >
          {!avatarUrl && parsedIcon.kind === "pack" && (
            <img src={parsedIcon.url} alt="" draggable={false} style={{ width: 28, height: 28, objectFit: "contain" }}/>
          )}
          {!avatarUrl && parsedIcon.kind === "emoji" && (
            <span style={{ fontSize: 22, lineHeight: 1 }}>{parsedIcon.emoji}</span>
          )}
          {!avatarUrl && parsedIcon.kind === "none" && (
            <span style={{ fontSize: 22, lineHeight: 1 }}>📅</span>
          )}
        </button>
        <button
          type="button"
          onClick={() => canModerate && setEditOpen(true)}
          disabled={!canModerate}
          title={canModerate ? "Управление приложением" : ""}
          style={{
            flex: 1, minWidth: 0,
            background: "transparent", border: "none", padding: 0,
            fontSize: 15, fontWeight: 700, color: "var(--ink)",
            cursor: canModerate ? "pointer" : "default",
            textAlign: "left", fontFamily: "inherit",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {topic.name}
          {canModerate && <span style={{ marginLeft: 6, fontSize: 11, color: "var(--mute)" }}>✎</span>}
        </button>
        {canModerate && (
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            title="Управление приложением"
            style={{
              background: "transparent", border: "1px solid var(--line)",
              borderRadius: 6, padding: "6px 10px", fontSize: 12,
              color: "var(--ink-2)", cursor: "pointer",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: -2 }}><path d="M4 7h8M16 7h4M4 17h4M12 17h8M14 4v6M8 14v6"/></svg>Настроить
          </button>
        )}
      </div>

      {/* Календарь выездов CRM (02.10.2026), а не страница старого дашборда */}
      <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <CrmFrame path="/visits?embed=1&view=cal" title="Расписание выездов"/>
      </div>

      {editOpen && (
        <EditGroupModal
          topic={topic}
          myUserId={myUserId}
          onClose={() => {
            setEditOpen(false);
            qc.invalidateQueries({ queryKey: ["chat-topics"] });
          }}
        />
      )}
    </div>
  );
}
